import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { recipesAPI, continentsAPI } from '../lib/api';
import { logger } from '../lib/logger';
import { Navbar } from '../components/layout/Navbar';
import { RecipeCard } from '../components/recipes/RecipeCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Filter, X, Utensils } from 'lucide-react';

const MEAL_TYPES = [
  { value: 'all', label: 'Tous les types' },
  { value: 'entree', label: 'Entrées' },
  { value: 'plat', label: 'Plats' },
  { value: 'dessert', label: 'Desserts' },
];

const DIFFICULTY_LEVELS = [
  { value: 'all', label: 'Toutes difficultés' },
  { value: '1', label: 'Très facile' },
  { value: '2', label: 'Facile' },
  { value: '3', label: 'Moyen' },
  { value: '4', label: 'Difficile' },
  { value: '5', label: 'Très difficile' },
];

const CONTINENTS = [
  { value: 'all', label: 'Tous les continents' },
  { value: 'africa', label: 'Afrique' },
  { value: 'asia', label: 'Asie' },
  { value: 'europe', label: 'Europe' },
  { value: 'north_america', label: 'Amérique du Nord' },
  { value: 'south_america', label: 'Amérique du Sud' },
  { value: 'oceania', label: 'Océanie' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [ingredient, setIngredient] = useState(searchParams.get('ingredient') || '');
  const [mealType, setMealType] = useState(searchParams.get('meal_type') || 'all');
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || 'all');
  const [continent, setContinent] = useState(searchParams.get('continent') || 'all');

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const params = {};
        if (searchQuery) params.search = searchQuery;
        if (ingredient) params.ingredient = ingredient;
        if (mealType !== 'all') params.meal_type = mealType;
        if (difficulty !== 'all') params.difficulty = parseInt(difficulty);
        if (continent !== 'all') params.continent = continent;

        const response = await recipesAPI.getAll(params);
        setRecipes(response.data);
      } catch (error) {
        logger.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, [searchQuery, ingredient, mealType, difficulty, continent]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (ingredient) params.set('ingredient', ingredient);
    if (mealType !== 'all') params.set('meal_type', mealType);
    if (difficulty !== 'all') params.set('difficulty', difficulty);
    if (continent !== 'all') params.set('continent', continent);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setIngredient('');
    setMealType('all');
    setDifficulty('all');
    setContinent('all');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || ingredient || mealType !== 'all' || difficulty !== 'all' || continent !== 'all';

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      {/* Header */}
      <section className="bg-worldplate-deep py-12 md:py-16" data-testid="search-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Rechercher des recettes
            </h1>

            {/* Main Search */}
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Rechercher par nom, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg rounded-full border-2 border-transparent bg-white focus:border-worldplate-primary"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-14 px-6 rounded-full bg-white border-2 border-transparent hover:border-worldplate-primary"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filtres
              </Button>
              <Button
                type="submit"
                className="h-14 px-8 rounded-full bg-worldplate-primary hover:bg-worldplate-primary/90 text-white font-bold"
                data-testid="search-submit-btn"
              >
                Rechercher
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Filters Panel */}
      {showFilters && (
        <motion.section
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white border-b border-stone-200 py-6"
          data-testid="filters-panel"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Ingredient */}
              <div>
                <label className="block text-sm font-medium text-worldplate-muted mb-2">
                  Par ingrédient
                </label>
                <Input
                  type="text"
                  placeholder="Ex: poulet, tomate..."
                  value={ingredient}
                  onChange={(e) => setIngredient(e.target.value)}
                  className="rounded-lg"
                  data-testid="ingredient-input"
                />
              </div>

              {/* Continent */}
              <div>
                <label className="block text-sm font-medium text-worldplate-muted mb-2">
                  Continent
                </label>
                <Select value={continent} onValueChange={setContinent}>
                  <SelectTrigger data-testid="continent-select">
                    <SelectValue placeholder="Tous les continents" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTINENTS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Meal Type */}
              <div>
                <label className="block text-sm font-medium text-worldplate-muted mb-2">
                  Type de plat
                </label>
                <Select value={mealType} onValueChange={setMealType}>
                  <SelectTrigger data-testid="mealtype-select">
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEAL_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-worldplate-muted mb-2">
                  Difficulté
                </label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger data-testid="difficulty-select">
                    <SelectValue placeholder="Toutes difficultés" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTY_LEVELS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-worldplate-error hover:text-worldplate-error/80 gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4" />
                  Effacer les filtres
                </Button>
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* Results */}
      <section className="py-12 md:py-16" data-testid="search-results">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Results count */}
          <div className="mb-8">
            <p className="text-worldplate-muted">
              {loading ? 'Recherche en cours...' : `${recipes.length} recette${recipes.length !== 1 ? 's' : ''} trouvée${recipes.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : recipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recipes.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-worldplate-subtle rounded-full flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-10 h-10 text-worldplate-muted" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-worldplate-deep mb-2">
                Aucune recette trouvée
              </h3>
              <p className="text-worldplate-muted mb-6">
                Essayez avec d'autres critères de recherche
              </p>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="rounded-full"
                data-testid="clear-search-btn"
              >
                Effacer la recherche
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
